import React from 'react';
import { PrimaryButton, DefaultButton, Panel, PanelType, Label, DatePicker, DayOfWeek, Dropdown, IDropdownOption, Stack, TextField, MessageBar, MessageBarType, ComboBox, IComboBox, IComboBoxOption, Toggle, IconButton, Link, Icon, SelectableOptionMenuItemType, TooltipHost, TagPicker, ITag, Dialog, DialogFooter } from '@fluentui/react';
import { IAssignmentDTO, ICertificationDTO, ICreateOrUpdateSupplierDTO, IMapData, ISBPPersonDTO, IStackMarker, WorkingAreaType } from '../../../models';
import styles from './AssignmentPanel.module.scss';
import { Services, IUserService } from '../../../services';
import App from '../../../App';
import { VisibilityControl } from '../../VisibilityControl/VisibilityControl';
import Utilities, { Status } from '../../../helpers/Utilities';
import LeafletMap from '../Maps/LeafletMap';
import DocumentationDialog from '../Documentation/DocumentationDialog';
import DelegatePanel from '../DelegatePanel/DelegatePanel';
import { IAssignmentFileDTO } from '../../../models/DTO/IAssignmentFileDTO';
import { TooltipComponent } from '../../../helpers/TooltipComponent';
import { AssignmentServiceProvider, IAssignmentService } from '../../../services/assignmentService';
import { UserServiceProvider } from '../../../services/userService';
import { isMobileOrTablet } from '../../../../src/components/VisibilityControl/UserAgentControl';
import L, { LatLngTuple } from 'leaflet';
import { withLanguage, WithLanguage } from '../../../LanguageContext';

//#region Interfaces
interface IAssignmentPanelProps extends WithLanguage {
    selectedAssignment?: IAssignmentDTO;
    onDismiss(): void;
    onChanged(assignment: IAssignmentDTO, showAssignmentPanel: boolean): void;
    allowSelectionOfSupplier: boolean;
}

interface IAssignmentPanelState {
    step: CreationSteps;
    mode: FormMode;
    currentItem: IAssignmentDTO;
    stackSumFromMap: number;
    stackTreeSpecices: string;
    canSubmit: boolean;
    extendedCreationRequired: boolean;
    SBP_Persons: ISBPPersonDTO[];
    suppliers: ICreateOrUpdateSupplierDTO[];
    certifications?: ICertificationDTO[];
    isSaving: boolean;
    showDelegatePanel: boolean;
    showValidationBox: boolean;
    validationMessage: string;
    showDocumentationOutdatedWarning: boolean;
    assignmentSavedAfterInactivity: boolean;
    trappingDate?: Date;
    isOffline?: boolean;
    showLeaveConfirmDialog: boolean;
    userOverrideEstimatedAmount: boolean;
}

enum CreationSteps {
    Assignment,
    Map,
    Documentation
}

export enum FormMode {
    New,
    Edit,
    ReadOnly
}
//#endregion

class AssignmentPanel extends React.Component<IAssignmentPanelProps, IAssignmentPanelState> {
    private assignmentService: IAssignmentService;
    private userService: IUserService;
    private fileInput_OwnMap;
    private fileInput_Declaration;

    //#region Properties
    constructor(props: IAssignmentPanelProps) {
        super(props);

        this.assignmentService = Services.AssignmentService.Initialize(new AssignmentServiceProvider());
        this.userService = Services.UserService.Initialize(new UserServiceProvider());

        // Set form mode for supplier
        let mode = props.allowSelectionOfSupplier ? FormMode.New : props.selectedAssignment ? (props.selectedAssignment.status == Status.WaitingForApproval || props.selectedAssignment.status == Status.Approved || props.selectedAssignment.status == Status.Rejected || props.selectedAssignment.status == Status.Completed) ? FormMode.ReadOnly : FormMode.Edit : FormMode.New;

        if (props.selectedAssignment && (App.IsUserInRole("DL") || App.IsUserInRole("Purchaser"))) {
            if (props.selectedAssignment.status == Status.Offline) {
                mode = FormMode.New;
            } else {
                mode = (props.selectedAssignment.status == Status.Approved || props.selectedAssignment.status == Status.Completed) ? FormMode.ReadOnly : FormMode.Edit;
            }
        }

        this.state = {
            step: CreationSteps.Assignment,
            mode: mode,
            canSubmit: false,
            extendedCreationRequired: false,
            currentItem: props.selectedAssignment ? props.selectedAssignment : {
                created: new Date(),
                status: Status.Open,
                hdaoNo: undefined,
                files: [],
                typeId: App.IsUserInRole("Purchaser") ? "Råtræ" : "Flis",
                biomassCategory: App.IsUserInRole("Purchaser") ? "Rundtræ" : undefined,
                ressourceType: App.IsUserInRole("Purchaser") ? "Rundtræ" : undefined,
                trappingDate: App.IsUserInRole("Purchaser") ? new Date(new Date().setDate(new Date().getDate() - 14)) : undefined
            },
            stackSumFromMap: 0,
            stackTreeSpecices: "",
            SBP_Persons: [] as ISBPPersonDTO[],
            suppliers: [] as ICreateOrUpdateSupplierDTO[],
            isSaving: false,
            showDelegatePanel: false,
            showValidationBox: false,
            validationMessage: "",
            showDocumentationOutdatedWarning: false,
            assignmentSavedAfterInactivity: false,
            isOffline: !navigator.onLine,
            showLeaveConfirmDialog: false,
            userOverrideEstimatedAmount: false,
        }

        this.load();
    }
    //#endregion

    //#region Methods
    public componentDidUpdate(prevProps: IAssignmentPanelProps, prevState: IAssignmentPanelState): void {
        if (prevState.currentItem.mapTimestamp != this.state.currentItem.mapTimestamp || prevState.currentItem.documentationTimestamp != this.state.currentItem.documentationTimestamp) {
            this.hasMapBeenChangedAfterDocumentationProcess();
        }
    }

    private filterSuggestedTags(filterText: string, selectedItems?: ITag[]): ITag[] {
        const res = filterText
            ? this.state.suppliers.filter(tag => tag.name.toLowerCase().indexOf(filterText.toLowerCase()) > -1)
            : this.state.suppliers;

        return res.map((v, i, a) => { return { key: v.id, name: v.name } });
    };

    private onMapSaved(mapData: IMapData, canEdit: boolean): void {
        if (!mapData.fixedTaskPosition && mapData.workingAreas && mapData.workingAreas.length > 0 && mapData.workingAreas[0].coordinates && mapData.workingAreas[0].coordinates.length > 0) {
            mapData.defaultPosition = [
                mapData.workingAreas[0].coordinates[0].lat,
                mapData.workingAreas[0].coordinates[0].lng
            ] as LatLngTuple;
        }

        let treeSpecices = this.getTreeSpecicesUsedInStacks(mapData);
        let timestamp = canEdit ? new Date() : this.state.currentItem.mapTimestamp;

        const stackSumChanged = this.state.stackSumFromMap !== mapData.stackSum;

        let newEstimatedAmount: number | undefined;
        if (stackSumChanged) {
            newEstimatedAmount = mapData.stackSum > 0 ? mapData.stackSum : this.state.currentItem.estimatedAmount;
        } else {
            newEstimatedAmount = this.state.currentItem.estimatedAmount;
        }

        this.setState({
            stackSumFromMap: mapData.stackSum,
            stackTreeSpecices: treeSpecices,
            currentItem: {
                ...this.state.currentItem,
                mapData: JSON.stringify(mapData),
                estimatedAmount: newEstimatedAmount,
                mapTimestamp: timestamp
            },
            step: CreationSteps.Assignment,
            userOverrideEstimatedAmount: stackSumChanged ? false : this.state.userOverrideEstimatedAmount
        });
    }

    //#endregion

    //#region Render action buttons
    private renderActionButtons() {
        const { t } = this.props;

        return (<div style={{ marginTop: "10px" }}>
            {(this.state.mode == FormMode.New || this.state.mode == FormMode.Edit) &&
                <React.Fragment>
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && this.state.showValidationBox &&
                        <MessageBar className={styles.validationBox} messageBarType={MessageBarType.severeWarning} isMultiline={true}>
                            <div dangerouslySetInnerHTML={{ __html: this.state.validationMessage }} />
                        </MessageBar>
                    }
                    <VisibilityControl name="disclaimer" visible={(!App.IsUserInRole("DL") && !App.IsUserInRole("Purchaser")) && this.state.currentItem.status == Status.Open}>
                        <Label>{t('assignmentPanel.disclaimer')}</Label>
                    </VisibilityControl>
                    <VisibilityControl name="Gem kladde" visible={(App.IsUserInRole("Supplier") || App.IsUserInRole("Purchaser")) && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.RequiresAdditionalInfo) || ((App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && this.state.currentItem.status != Status.Approved && this.state.currentItem.status != Status.Rejected && this.state.currentItem.status != Status.RequiresAdditionalInfo)}>
                        <PrimaryButton
                            className={styles.button}
                            disabled={this.state.isSaving}
                            onClick={() => { this.onAddOrUpdateAssignment(this.state.currentItem) }}
                        >
                            {t('assignmentPanel.saveDraft')}
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Overfør til Dalgas godkendelse" visible={App.IsUserInRole("Supplier") && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.RequiresAdditionalInfo) && !this.props.allowSelectionOfSupplier}>
                        <PrimaryButton
                            className={styles.button}
                            disabled={this.state.isSaving}
                            onClick={async () => { let currentItem = { ...this.state.currentItem }; currentItem.status = Status.WaitingForApproval; this.onAddOrUpdateAssignment(currentItem); }}
                        >
                            {t('assignmentPanel.transferToApproval')}
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Godkend" visible={(App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && (this.state.currentItem.status == Status.WaitingForApproval || this.state.currentItem.status == Status.RequiresAdditionalInfo || this.props.allowSelectionOfSupplier)}>
                        <PrimaryButton
                            className={styles.approveButton}
                            onClick={async () => {
                                if (this.validateForm_CanApprove()) {
                                    let currentItem = { ...this.state.currentItem };
                                    currentItem.status = "Godkendt";
                                    currentItem.modifiedBy = App.Username;
                                    this.onAddOrUpdateAssignment(currentItem);
                                }
                            }}
                            disabled={this.state.isSaving || this.state.showDocumentationOutdatedWarning}
                        >
                            Godkend
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Afvis" visible={App.IsUserInRole("DL") && (this.state.currentItem.status == Status.WaitingForApproval)}>
                        <React.Fragment>
                            <PrimaryButton
                                className={styles.rejectButton}
                                disabled={this.state.isSaving}
                                onClick={async () => {
                                    let currentItem = { ...this.state.currentItem };
                                    currentItem.status = "Afvist";

                                    this.onAddOrUpdateAssignment(currentItem);
                                }}
                            >
                                Afvis
                            </PrimaryButton>
                            <PrimaryButton
                                className={styles.requiresAdditionalInfo}
                                disabled={this.state.isSaving}
                                onClick={async () => {
                                    let currentItem = { ...this.state.currentItem };
                                    currentItem.status = "Kræver yderligere info";

                                    this.onAddOrUpdateAssignment(currentItem);
                                }
                                }
                            >
                                Retur til leverandør
                            </PrimaryButton>
                            <PrimaryButton
                                className={styles.delegateInternally}
                                disabled={this.state.isSaving}
                                onClick={async () => { this.setState({ showDelegatePanel: true }); }}>
                                Deleger internt
                            </PrimaryButton>
                        </React.Fragment>
                    </VisibilityControl>
                </React.Fragment>
            }
            <VisibilityControl
                name="Slet opgave"
                visible={
                    (((App.IsUserInRole("Purchaser") || App.IsUserInRole("DL")) && (this.state.mode == FormMode.Edit || this.state.mode == FormMode.ReadOnly)) && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.Rejected || this.state.currentItem.status == Status.WaitingForApproval || this.state.currentItem.status == Status.Offline)) ||
                    (App.IsUserInRole("Supplier") && ((this.state.mode == FormMode.Edit && this.state.currentItem.status == Status.Open) || this.state.currentItem.status == Status.RequiresAdditionalInfo))
                }
            >
                <PrimaryButton
                    className={styles.deleteButton}
                    onClick={async () => {
                        if (this.state.currentItem.status === Status.Offline) {
                            await this.handleDeleteOfflineAssignment();
                        } else {
                            await this.deleteAssignment();
                        }
                    }}
                >
                    Slet opgave
                </PrimaryButton>
            </VisibilityControl>
            <VisibilityControl name="Send retur til DL" visible={App.IsUserInRole("Admin") && (this.state.currentItem.status == Status.Approved)}>
                <PrimaryButton
                    className={styles.rejectButton}
                    disabled={this.state.isSaving}
                    onClick={async () => {
                        if (window.confirm("Er du sikker på du vil sende opgaven tilbage til driftslederen?")) {
                            this.onAddOrUpdateAssignment(this.state.currentItem);
                        }
                    }}
                >
                    Send retur til DL (!)
                </PrimaryButton>
            </VisibilityControl>
            <DefaultButton onClick={() => this.props.onDismiss()}>{t('common.cancel')}</DefaultButton>
        </div>);
    }
    //#endregion

    //#region Render Panel
    public render(): React.ReactElement<IAssignmentPanelProps> {
        const { t } = this.props;
        const isOffline = this.props.selectedAssignment?.status === Status.Offline || this.state.currentItem?.status === Status.Offline;

        return (<>
            <Panel
                headerText={this.props.selectedAssignment ? "Rediger opgave" : "Opret ny opgave"}
                isOpen={true}
                className={styles.createPanel}
                isLightDismiss={false}
                isBlocking={true}
                onDismiss={() => { this.props.onDismiss(); }}
                closeButtonAriaLabel="Luk opgavepanel"
                type={PanelType.medium}
                onRenderFooterContent={!isMobileOrTablet() ? this.renderActionButtons.bind(this) : undefined}
                styles={
                    isMobileOrTablet() ? {
                        main: {
                            width: '75vw',
                            minWidth: '75vw',
                            maxWidth: '100vw',
                            height: '100%',
                            maxHeight: '100%',
                            overflowY: 'auto',
                            KeyboardAvoidingView: { behavior: 'padding' },
                            TouchableWithoutFeedback: { Keyboard: 'dismiss' }
                        }
                    } : undefined
                }
            >
                <Stack style={isMobileOrTablet() ? { paddingBottom: 120 } : undefined}>
                    {isMobileOrTablet() && (
                        <div
                            className={styles.stickyActionBar}>
                            {this.renderActionButtons()}
                        </div>
                    )}
                    {this.state.isOffline && (
                        <MessageBar messageBarType={MessageBarType.severeWarning}>
                            {t('dashboard.offlineMessage')}
                        </MessageBar>
                    )}
                    {this.state.mode != FormMode.New &&
                        <React.Fragment>
                            <MessageBar messageBarType={this.state.currentItem.status == Status.Rejected ? MessageBarType.error : MessageBarType.info} isMultiline={false}>
                                <div>Opgavetype: {this.state.currentItem.typeId}</div>
                                {!App.IsUserInRole("Supplier") && <div>Leverandør: {this.state.currentItem.supplierName}</div>}
                                <div>Opgave oprettet af: {this.state.currentItem.createdBy}</div>
                                <span>Status: {this.state.currentItem.status}</span>
                                {
                                    this.state.currentItem.status == Status.Approved ? <span>&nbsp;{this.state.currentItem.approvedDate?.toLocaleDateString()} af {this.state.currentItem.modifiedBy}</span> : <></>
                                }
                            </MessageBar>
                        </React.Fragment>
                    }
                    {(this.props.allowSelectionOfSupplier || (this.state.currentItem.status === Status.Offline)) &&
                        <>
                            <Label required>Leverandør</Label>
                            <TagPicker
                                itemLimit={1}
                                selectionAriaLabel="Vælg leverandør"
                                onResolveSuggestions={this.filterSuggestedTags.bind(this)}
                                getTextFromItem={(item: ITag) => item.name}
                                pickerSuggestionsProps={{
                                    suggestionsHeaderText: 'Leverandører',
                                    noResultsFoundText: 'Intet match fundet.',
                                }}
                                inputProps={{
                                    placeholder: t('userManagement.searchSupplier')
                                }}
                                disabled={this.state.mode === FormMode.ReadOnly || this.state.isOffline}
                                onChange={this.onSupplierChange.bind(this)}
                            />
                        </>
                    }
                    <TextField
                        required={App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")}
                        onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.taskName.name')} description={t('tooltip.assignmentPanel.taskName.description')} /> }}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        placeholder={t('assignmentPanel.taskNamePlaceholder')}
                        value={this.state.currentItem.orderName}
                        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { this.updateProperty({ orderName: this.trimOrderName(newValue + "") }) }}
                        onBlur={() => { if (!navigator.onLine) { this.saveAssignmentOffline(); } }}
                    />
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance") || App.IsUserInRole("Purchaser")) &&
                        <React.Fragment>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="end">
                                <Stack.Item grow={8}>
                                    <TextField
                                        value={this.state.currentItem.hdaoNo != undefined ? this.state.currentItem.hdaoNo.toLocaleString() : undefined}
                                        label={t('assignmentPanel.dalgasAOLabel')}
                                        required={true}
                                        disabled={true}
                                        multiline={false}
                                        onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { this.updateProperty({ hdaoNo: parseInt(newValue + "") }) })}
                                    />
                                </Stack.Item>
                                <Stack.Item grow={1}>
                                    <IconButton disabled={this.state.currentItem.hdaoNo != undefined || this.state.isOffline} iconProps={{ iconName: "PageLock" }} title="Udtag Dalgas AO nr." ariaLabel="Udtag Dalgas AO nr." onClick={this.takeNextPINFONo.bind(this)} />
                                </Stack.Item>
                            </Stack>
                            <TextField
                                value={this.state.currentItem.projectNo}
                                label={t('assignmentPanel.projectNumberLabel')}
                                required={true}
                                disabled={this.state.mode == FormMode.ReadOnly || this.state.isOffline}
                                multiline={false}
                                onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { if (newValue && newValue?.length <= 20) this.updateProperty({ projectNo: newValue }) })}
                                onBlur={() => { if (!navigator.onLine) { this.saveAssignmentOffline(); } }}
                            />
                        </React.Fragment>
                    }
                    {App.IsUserInRole("Supplier") &&
                        <TextField
                            onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.dalgasWorkOrder.name')} description={t('tooltip.assignmentPanel.dalgasWorkOrder.description')} /> }}
                            value={this.state.currentItem.hdaoNo != undefined ? this.state.currentItem.hdaoNo.toLocaleString() : undefined}
                            disabled={true}
                            multiline={false}
                        />
                    }
                    {(this.state.certifications && this.state.certifications.length > 1) &&
                        <Dropdown
                            placeholder={t('common.selectFromList')}
                            disabled={this.state.mode == FormMode.ReadOnly}
                            onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.certified.name')} description={t('tooltip.assignmentPanel.certified.description')} /> }}
                            selectedKey={this.state.currentItem.certification == undefined ? "" : this.state.currentItem.certification.id}
                            options={this.state.certifications ? this.state.certifications.map((value: ICertificationDTO) => { return { key: value.id, text: value.name } }) : [] as IDropdownOption[]}
                            onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => { this.setState({ currentItem: { ...this.state.currentItem, certification: this.state.certifications?.filter(d => d.id == option?.key)[0] } }, () => { this.validateForm(); }) }}
                        />
                    }
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance") || App.IsUserInRole("Supplier")) &&
                        <Dropdown
                            required={App.IsUserInRole("DL")}
                            placeholder={t('common.selectFromList')}
                            disabled={this.state.mode == FormMode.ReadOnly}
                            onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.chipType.name')} description={t('tooltip.assignmentPanel.chipType.description')} /> }}
                            selectedKey={this.state.currentItem.biomassCategory}
                            options={[
                                { key: 'Skov: rundtræ/stammetræ', text: 'Skov: rundtræ/stammetræ' },
                                { key: 'Skov: restprodukter fra skovbrug inkl. alle tyndinger', text: 'Skov: restprodukter fra skovbrug inkl. alle tyndinger' },
                                { key: 'Skov: afdrift kun til energi (afdrift hvor alt materiale går til flis)', text: 'Skov: afdrift kun til energi (afdrift hvor alt materiale går til flis)' },
                                { key: 'Ikke skov: læhegn, småbevoksninger under 0,5ha og naturpleje', text: 'Ikkeskov: læhegn, småbevoksninger under 0,5ha og naturpleje' },
                                { key: 'Træaffald inkl. fra omkring infrastruktur, byggemodning, have-, park-,  vej- og bytræer', text: 'Træaffald inkl. fra omkring infrastruktur, byggemodning, have-, park-,  vej- og bytræer' },
                                { key: 'Landbrug: Energipil og energipoppel', text: 'Landbrug: Energipil og energipoppel' },
                                { key: 'Resttræ fra landbrugsarealer inkl juletræer og frugtplantager', text: 'Resttræ fra landbrugsarealer inkl juletræer og frugtplantager' },
                                { key: 'Træindustri: restprodukter fra træindustri', text: 'Træindustri: restprodukter fra træindustri' },
                                ...(App.IsUserInRole("Admin") || App.IsUserInRole("DL")
                                    ? [{ key: 'Rundtræ', text: 'Rundtræ' }]
                                    : [])
                            ]}
                            onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => { this.updateProperty({ biomassCategory: option?.key + "" }); }}
                        />
                    }
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Purchaser") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance") || App.IsUserInRole("Supplier")) &&
                        <TextField
                            required={App.IsUserInRole("DL") && this.state.currentItem.typeId !== "Råtræ"}
                            multiline={true}
                            disabled={this.state.mode == FormMode.ReadOnly}
                            rows={6}
                            onRenderLabel={() => { 
                                const isRaatrae = this.state.currentItem.typeId === "Råtræ";
                                const labelName = isRaatrae ? t('tooltip.assignmentPanel.remarks.name') : t('tooltip.assignmentPanel.taskDescription.name');
                                const description = isRaatrae ? t('tooltip.assignmentPanel.remarks.description') : t('tooltip.assignmentPanel.taskDescription.description');
                                return <TooltipComponent name={labelName} description={description} /> 
                            }}
                            placeholder={t('assignmentPanel.additionalInfoPlaceholder')}
                            value={this.state.currentItem.description}
                            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { this.updateProperty({ description: newValue }) }}
                            onBlur={() => { if (!navigator.onLine) { this.saveAssignmentOffline(); } }}
                        />
                    }
                    <TooltipComponent name={t('tooltip.assignmentPanel.fellingDate.name')} description={t('tooltip.assignmentPanel.fellingDate.description')} />
                    <DatePicker
                        firstDayOfWeek={DayOfWeek.Monday}
                        placeholder={t('assignmentPanel.trappingDatePlaceholder')}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        isRequired={App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")}
                        value={
                            App.IsUserInRole("Purchaser")
                                ? this.state.currentItem.trappingDate || new Date(new Date().setDate(new Date().getDate() - 14))
                                : this.state.currentItem.trappingDate || undefined
                        }
                        formatDate={Utilities.FormatDate}
                        onSelectDate={(val) => {
                            this.updateProperty({ trappingDate: val != null ? val : undefined });
                            { if (!navigator.onLine) { this.saveAssignmentOffline(); } }
                        }}
                        strings={Utilities.DayPickerStrings}
                    />
                    <PrimaryButton className={styles.defaultButton} onClick={() => { this.setState({ step: CreationSteps.Map }); }} disabled={this.state.isOffline}>
                        <TooltipComponent name={(this.state.currentItem.status == Status.Approved || this.state.currentItem.status == Status.Rejected || this.state.currentItem.status == Status.WaitingForApproval || this.state.currentItem.status == Status.Completed) ? t('tooltip.assignmentPanel.openMap.name') : t('tooltip.assignmentPanel.drawMap.name')} description={t('tooltip.assignmentPanel.drawMap.description')} />
                    </PrimaryButton>
                    {this.state.step == CreationSteps.Map &&
                        <LeafletMap currentItem={this.state.currentItem}
                            onDismiss={(currentItem: IAssignmentDTO) => { this.setState({ step: CreationSteps.Assignment }); }}
                            onSaveClicked={(mapData: IMapData, canEdit: boolean) => {
                                this.onMapSaved(mapData, canEdit);
                            }}
                            onSaveDueToInactivity={async (mapData: IMapData) => {
                                this.onMapSaved(mapData, true);
                                this.onAddOrUpdateAssignment(this.state.currentItem, true);
                                this.setState({ assignmentSavedAfterInactivity: true });
                            }}
                        />
                    }

                    {(this.state.mode == FormMode.New || this.state.mode == FormMode.Edit) &&
                        <React.Fragment>
                            <PrimaryButton className={styles.defaultButton} onClick={() => { this.fileInput_OwnMap.click() }} disabled={this.state.isOffline}>
                                <TooltipComponent
                                    name={App.IsUserInRole("Purchaser") ? t('tooltip.assignmentPanel.uploadFromCameraRoll.name') : t('tooltip.assignmentPanel.uploadOwnMap.name')}
                                    description={App.IsUserInRole("Purchaser") ? t('tooltip.assignmentPanel.uploadFromCameraRoll.description') : t('tooltip.assignmentPanel.uploadOwnMap.description')} />
                            </PrimaryButton>
                            <input
                                style={{ display: "none" }}
                                type="file"
                                multiple={true}
                                ref={fileInput => this.fileInput_OwnMap = fileInput}
                                onChange={async (event) => {
                                    if (event.target.files) {
                                        await this.addFiles(event.target.files, "OwnMap");
                                    }
                                }}
                                disabled={this.state.isOffline}
                            />
                        </React.Fragment>
                    }
                    {this.state.mode != FormMode.ReadOnly &&
                        <PrimaryButton className={styles.defaultButton} onClick={async () => { if (this.state.currentItem.id == undefined) await this.preSaveAssignment(); this.setState({ step: CreationSteps.Documentation }); }} disabled={(App.IsUserInRole("DL") && !this.state.currentItem.supplierID) || this.state.currentItem.mapData == undefined || (JSON.parse(this.state.currentItem.mapData) as IMapData).fixedTaskPosition == undefined}>
                            <TooltipComponent name={t('tooltip.assignmentPanel.createDocumentation.name')} description={t('tooltip.assignmentPanel.createDocumentation.description')} />
                        </PrimaryButton>
                    }
                    {this.state.step == CreationSteps.Documentation && <DocumentationDialog currentItem={this.state.currentItem} onDismiss={() => { this.setState({ step: CreationSteps.Assignment }); }} onSaved={(newFiles: IAssignmentFileDTO[]) => {
                        let currentItem = this.state.currentItem;

                        let allFiles = currentItem.files?.filter(d => d.type != "Documentation");
                        allFiles?.push(...newFiles);

                        this.setState({ currentItem: { ...this.state.currentItem, files: allFiles, documentationTimestamp: new Date() }, step: CreationSteps.Assignment });
                    }}
                    />}
                    <Label>Dokumentation</Label>
                    <div>
                        {this.state.currentItem.files?.filter(d => d.type == "OwnMap" || d.type == "Documentation")?.map((v, i, a) => {
                            return <div key={`documentationFile_${i}`}>
                                {v.isNew ?
                                    <Label className={styles.newLink}>{v.name}</Label> :
                                    <div><Link onClick={async () => { this.downloadFile(v.name); }}>{v.name} ({v.type == "OwnMap" ? "eget kort" : "Dalgas dokumentation"})</Link> {v.type == "OwnMap" && <IconButton onClick={() => { if (window.confirm('Er du sikker på, at du ønsker at slette denne fil? Handlingen kan ikke fortrydes.') == true) this.deleteFile(v); }} iconProps={{ iconName: 'Delete' }} title="Slet" />}</div>
                                }
                            </div>
                        })
                        }
                        {App.IsUserInRole("DL") && this.state.currentItem.files?.filter(d => d.type == "OwnMap").length == 1 &&
                            <React.Fragment>
                                <Label>Uploadet materiale er godkendt som bæredygtighedsdokumentation</Label>
                                <div className={styles.toggleOptions}>
                                    <Label className={styles.toggleLabelLeft}>Nej</Label>
                                    <Toggle
                                        disabled={this.state.mode == FormMode.ReadOnly}
                                        checked={this.state.currentItem.isOwnMapApproved}
                                        onChange={(ev, checked?: boolean) => { this.updateProperty({ isOwnMapApproved: checked }) }}
                                    />
                                    <Label className={styles.toggleLabelRight}>Ja</Label>
                                </div>
                            </React.Fragment>
                        }
                    </div>
                    {this.state.showDocumentationOutdatedWarning && <div className={styles.documentationOutdated}><Icon iconName='Important' /><span>Dokumentationen er forældet!</span></div>}

                    {this.state.mode != FormMode.New && this.state.currentItem.files?.length == 0 && <div>Der er <u>ikke</u> uploadet eget kort</div>}
                    {(!App.IsUserInRole("Purchaser")) &&
                        <TextField
                            value={this.state.currentItem.estimatedAmount ? this.state.currentItem.estimatedAmount + "" : undefined}
                            onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.estimatedAmount.name')} description={t('tooltip.assignmentPanel.estimatedAmount.description')} /> }}
                            description={this.getVolumeDescription()}
                            placeholder={t('assignmentPanel.estimatedAmountPlaceholder')}
                            disabled={this.state.mode == FormMode.ReadOnly}
                            suffix={t('common.rm')}
                            required={App.IsUserInRole("DL")}
                            onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { 
                                const newAmount = parseFloat(newValue + "");
                                const shouldOverride = this.state.stackSumFromMap > 0;
                                this.updateProperty({ estimatedAmount: newAmount });
                                if (shouldOverride) {
                                    this.setState({ userOverrideEstimatedAmount: true });
                                }
                            })}
                        />
                    }
                    {(this.state.extendedCreationRequired || App.IsUserInRole("Purchaser")) &&
                        <div>
                            {!App.IsUserInRole("Purchaser") &&
                                <Stack>
                                    <div>
                                        {this.state.currentItem.typeId === "Flis" ? (
                                            <>
                                                <ComboBox
                                                    selectedKey={this.state.currentItem.trappingWorkPerformedByEntrepreneur?.toLocaleLowerCase()}
                                                    onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.fellingWorkPerformedBy.name')} description={t('tooltip.assignmentPanel.fellingWorkPerformedBy.description')} /> }}
                                                    autoComplete="on"
                                                    allowFreeform={true}
                                                    disabled={this.state.mode == FormMode.ReadOnly}
                                                    options={this.state.SBP_Persons.map((value: ISBPPersonDTO) => { return { key: value.id?.toLocaleLowerCase() + "", text: value.name + "" } })}
                                                    onChange={this.onSBPPersonSelectionChange.bind(this, "trappingWorkPerformedByEntrepreneur")}
                                                />
                                                {this.state.currentItem.trappingWorkPerformedByEntrepreneur && this.state.SBP_Persons.find(x => x.id == this.state.currentItem.trappingWorkPerformedByEntrepreneur)?.name?.endsWith("(indtastet manuelt)") &&
                                                    <p style={{ color: 'red', marginBottom: 5 }}>OBS. skal denne person ikke oprettes?</p>
                                                }
                                            </>
                                        ) : (
                                            <TextField
                                                onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.fellingWorkPerformedBy.name')} description={t('tooltip.assignmentPanel.fellingWorkPerformedBy.description')} /> }}
                                                autoComplete="on"
                                                disabled={this.state.mode == FormMode.ReadOnly}
                                                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                                    this.updateProperty({ trappingWorkPerformedByEntrepreneur: newValue });
                                                }}
                                                value={this.state.currentItem.trappingWorkPerformedByEntrepreneur || ""}
                                            />
                                        )}

                                        {this.state.currentItem.typeId === "Flis" ? (
                                            <>
                                                <ComboBox
                                                    selectedKey={this.state.currentItem.extractionPerformedByEntrepreneur?.toLocaleLowerCase()}
                                                    onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.extractionPerformedBy.name')} description={t('tooltip.assignmentPanel.extractionPerformedBy.description')} /> }}
                                                    autoComplete="on"
                                                    allowFreeform={true}
                                                    disabled={this.state.mode == FormMode.ReadOnly}
                                                    options={this.state.SBP_Persons.map((value: ISBPPersonDTO) => { return { key: value.id?.toLocaleLowerCase() + "", text: value.name + "" } })}
                                                    onChange={this.onSBPPersonSelectionChange.bind(this, "extractionPerformedByEntrepreneur")}
                                                />
                                                {this.state.currentItem.extractionPerformedByEntrepreneur && this.state.SBP_Persons.find(x => x.id == this.state.currentItem.extractionPerformedByEntrepreneur)?.name?.endsWith("(indtastet manuelt)") &&
                                                    <p style={{ color: 'red', marginBottom: 5 }}>OBS. skal denne person ikke oprettes?</p>
                                                }
                                            </>
                                        ) : (
                                            <TextField
                                                onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.extractionPerformedBy.name')} description={t('tooltip.assignmentPanel.extractionPerformedBy.description')} /> }}
                                                autoComplete="on"
                                                disabled={this.state.mode == FormMode.ReadOnly}
                                                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                                    this.updateProperty({ extractionPerformedByEntrepreneur: newValue });
                                                }}
                                                value={this.state.currentItem.extractionPerformedByEntrepreneur || ""}
                                            />
                                        )}

                                        {this.state.currentItem.typeId === "Flis" ? (
                                            <>
                                                <ComboBox
                                                    selectedKey={this.state.currentItem.woodchippingPerformedByEntrepreneur?.toLocaleLowerCase()}
                                                    onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.chippingPerformedBy.name')} description={t('tooltip.assignmentPanel.chippingPerformedBy.description')} /> }}
                                                    autoComplete="on"
                                                    allowFreeform={true}
                                                    disabled={this.state.mode == FormMode.ReadOnly}
                                                    options={this.state.SBP_Persons.map((value: ISBPPersonDTO) => { return { key: value.id?.toLocaleLowerCase() + "", text: value.name + "" } })}
                                                    onChange={this.onSBPPersonSelectionChange.bind(this, "woodchippingPerformedByEntrepreneur")}
                                                />
                                                {this.state.currentItem.woodchippingPerformedByEntrepreneur && this.state.SBP_Persons.find(x => x.id == this.state.currentItem.woodchippingPerformedByEntrepreneur)?.name?.endsWith("(indtastet manuelt)") &&
                                                    <p style={{ color: 'red', marginBottom: 5 }}>OBS. skal denne person ikke oprettes?</p>
                                                }
                                            </>
                                        ) : (
                                            <TextField
                                                onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.chippingPerformedBy.name')} description={t('tooltip.assignmentPanel.chippingPerformedBy.description')} /> }}
                                                autoComplete="on"
                                                disabled={this.state.mode == FormMode.ReadOnly}
                                                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                                    this.updateProperty({ woodchippingPerformedByEntrepreneur: newValue });
                                                }}
                                                value={this.state.currentItem.woodchippingPerformedByEntrepreneur || ""}
                                            />
                                        )}
                                    </div>
                                </Stack>
                            }
                            {(this.state.currentItem.typeId == "Råtræ" || (this.state.currentItem.typeId == "Flis" && this.state.extendedCreationRequired)) &&
                                <Stack>
                                    <div>
                                        <TooltipComponent name={t('tooltip.assignmentPanel.natureCultureDamage.name')} description={t('tooltip.assignmentPanel.natureCultureDamage.description')} />
                                        <Label></Label>
                                        <div className={styles.toggleOptions}>
                                            <Label className={styles.toggleLabelLeft}>{t('common.no')}</Label>
                                            <Toggle
                                                disabled={this.state.mode == FormMode.ReadOnly}
                                                checked={this.state.currentItem.hasNatureOrCultureBeenDamage}
                                                onChange={(ev, checked?: boolean) => { this.updateProperty({ hasNatureOrCultureBeenDamage: checked }) }}
                                            />
                                            <Label className={styles.toggleLabelRight}>{t('common.yes')}</Label>
                                        </div>
                                    </div>
                                    {this.state.currentItem.hasNatureOrCultureBeenDamage &&
                                        <React.Fragment>
                                            <Label>Er skaden efterfølgede blevet udbedret?</Label>
                                            <div className={styles.toggleOptions}>
                                                <Label className={styles.toggleLabelLeft}>{t('common.no')}</Label>
                                                <Toggle
                                                    disabled={this.state.mode == FormMode.ReadOnly}
                                                    checked={this.state.currentItem.hasTheDamageSubsequentlyBeenRepaired}
                                                    onChange={(ev, checked?: boolean) => { this.updateProperty({ hasTheDamageSubsequentlyBeenRepaired: checked }) }}
                                                />
                                                <Label className={styles.toggleLabelRight}>{t('common.yes')}</Label>
                                            </div>
                                        </React.Fragment>
                                    }
                                    <TooltipComponent name={t('tooltip.assignmentPanel.nonForestClearing.name')} description={t('tooltip.assignmentPanel.nonForestClearing.description')} />
                                    <div className={styles.toggleOptions}>
                                        <Label className={styles.toggleLabelLeft}>{t('common.no')}</Label>
                                        <Toggle
                                            disabled={this.state.mode == FormMode.ReadOnly}
                                            checked={this.state.currentItem.continuedTreeGrowthOnTheAreaOrReplanting}
                                            onChange={(ev, checked?: boolean) => { this.updateProperty({ continuedTreeGrowthOnTheAreaOrReplanting: checked }) }}
                                        />
                                        <Label className={styles.toggleLabelRight}>{t('common.yes')}</Label>
                                    </div>
                                    {this.state.currentItem.continuedTreeGrowthOnTheAreaOrReplanting &&
                                        <React.Fragment>
                                            <div><i>Afdrevne arealer skal reetableres med træer. Der skal udfyldes og vedhæftes en ejererklæring</i></div>
                                            {(this.state.mode == FormMode.New || this.state.mode == FormMode.Edit) &&
                                                <React.Fragment>
                                                    <PrimaryButton text={t('assignmentPanel.uploadDeclaration')} style={{ marginTop: "10px" }} onClick={() => { this.fileInput_Declaration.click() }} />
                                                    <input
                                                        style={{ display: "none" }}
                                                        type="file"
                                                        multiple={false}
                                                        ref={fileInput => this.fileInput_Declaration = fileInput}
                                                        onChange={async (event) => {
                                                            if (event.target.files) {
                                                                await this.addFiles(event.target.files, "Declaration");
                                                            }
                                                        }}
                                                    />
                                                </React.Fragment>
                                            }
                                            <div>
                                                <TooltipComponent name={t('tooltip.assignmentPanel.ownerDeclaration.name')} description={t('tooltip.assignmentPanel.ownerDeclaration.description')} />
                                                {this.state.currentItem.files?.filter(d => d.type == "Declaration")?.length == 0 && <div>Der er <u>ikke</u> uploadet ejererklæring</div>}
                                                {this.state.currentItem.files?.filter(d => d.type == "Declaration")?.map((v, i, a) => {
                                                    return <div key={`declarationFile_${i}`}>
                                                        {v.isNew ? <Label className={styles.newLink}>{v.name}</Label> : <Link onClick={async () => { this.downloadFile(v.name); }}>{v.name}</Link>}
                                                    </div>
                                                })}
                                            </div>
                                        </React.Fragment>
                                    }
                                </Stack>
                            }
                        </div>
                    }
                    {(this.state.currentItem.status == Status.RequiresAdditionalInfo || this.state.currentItem.status == Status.Rejected || App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) &&
                        <TextField
                            value={this.state.currentItem.comment}
                            label={t('assignmentPanel.dalgasCommentsLabel')}
                            multiline={true}
                            rows={6}
                            disabled={!App.IsUserInRole("DL") || this.state.mode == FormMode.ReadOnly}
                            onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => { this.updateProperty({ comment: newValue }) })}
                        />
                    }
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) &&
                        <React.Fragment>
                            <Dropdown
                                required={App.IsUserInRole("DL")}
                                placeholder={t('common.selectFromList')}
                                disabled={this.state.mode == FormMode.ReadOnly}
                                label={t('assignmentPanel.resourceTypeLabel')}
                                selectedKey={this.state.currentItem.ressourceType}
                                options={[
                                    { key: 'Flis jord', text: 'Flis jord' },
                                    { key: 'Grot', text: 'Grot' },
                                    { key: 'Fældeudkørt', text: 'Fældeudkørt' },
                                    { key: 'Skovlager', text: 'Skovlager' },
                                    { key: 'Rundtræ', text: 'Rundtræ' }
                                ]}
                                onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => { this.updateProperty({ ressourceType: option?.key.toString() }) }}
                            />
                        </React.Fragment>
                    }
                    {(App.IsUserInRole("Purchaser")) &&
                        <React.Fragment>
                            <TextField
                                label={t('assignmentPanel.verificationNoLabel')}
                                multiline={false}
                                value={this.state.currentItem.verificationNo || ""}
                                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                    if (/^\d*$/.test(newValue || "")) {
                                        this.updateProperty({ verificationNo: newValue });
                                    }
                                }}
                                onBlur={() => { if (!navigator.onLine) { this.saveAssignmentOffline(); } }}
                            />
                            <TextField
                                label={t('assignmentPanel.refNoLabel')}
                                multiline={false}
                                value={this.state.currentItem.refNo || ""}
                                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                    if (/^\d*$/.test(newValue || "")) {
                                        this.updateProperty({ refNo: newValue });
                                    }
                                }}
                                onBlur={() => { if (!navigator.onLine) { this.saveAssignmentOffline(); } }}
                            />
                        </React.Fragment>
                    }
                </Stack>
                {this.state.showDelegatePanel && <DelegatePanel selectedAssignment={this.state.currentItem} onChanged={() => { this.props.onChanged(this.state.currentItem, false); }} onDismiss={() => { this.setState({ showDelegatePanel: false }); }} />}
            </Panel>
            {this.state.assignmentSavedAfterInactivity &&
                <Dialog hidden={false} minWidth={250}>
                    Dit arbejde på kortet er <b>automatisk</b> blevet gemt på opgaven grundet inaktivitet. Du kan fortsætte hvor du slap ved at åbne opgaven igen.
                    <DialogFooter>
                        <PrimaryButton text={t('common.understood')} onClick={() => { this.setState({ assignmentSavedAfterInactivity: false }); }} />
                    </DialogFooter>
                </Dialog>}
            {this.state.showLeaveConfirmDialog && (
                <div className="leave-confirm-dialog" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 9999 }}>
                    <div style={{ background: 'white', padding: 32, borderRadius: 8, maxWidth: 400, margin: '10vh auto', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <h3>Er du sikker på, at du vil forlade siden?</h3>
                        <p>Du har ikke gemt dine ændringer. Hvis du forlader siden, vil de gå tabt.</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                            <button onClick={() => this.setState({ showLeaveConfirmDialog: false })}>Bliv på siden</button>
                            <button onClick={this.confirmLeave}>Forlad og kassér ændringer</button>
                        </div>
                    </div>
                </div>
            )}
        </>
        );
    }
    //#endregion

    //#region Load
    private async load() {
        let certifications = [] as ICertificationDTO[];
        let supplierId = this.state.currentItem.supplierID ? this.state.currentItem.supplierID : App.SupplierId;

        if (supplierId) {
            certifications = await this.userService.GetCertificationsForSupplier(supplierId);
        }

        if (this.props.allowSelectionOfSupplier ||
            (this.state.currentItem.status === Status.Offline && !this.state.isOffline)) {
            let suppliers: ICreateOrUpdateSupplierDTO[] = await this.userService.GetSuppliers();
            this.setState({ suppliers: suppliers, certifications: certifications });
        } else {
            this.setState({ certifications: certifications });
        }

        // If existing item, calculate stack sum from MapData
        if (this.props.selectedAssignment && this.state.currentItem.mapData) {
            let mapData: IMapData = JSON.parse(this.state.currentItem.mapData) as IMapData;
            let treeSpecices = this.getTreeSpecicesUsedInStacks(mapData);

            this.setState({ stackSumFromMap: mapData.stackSum, stackTreeSpecices: treeSpecices });
        }

        if (this.props.selectedAssignment) {
            let files = await this.assignmentService.GetFilesForAssignment(this.props.selectedAssignment);
            if (files.length > 0) {
                this.setState({ currentItem: { ...this.state.currentItem, files: files } });
            }
        }

        this.validateForm();
        this.hasMapBeenChangedAfterDocumentationProcess();
    }

    //#region Map
    private isMapValidForApproval(): boolean {
        let mapData: IMapData | undefined = undefined;

        if (this.state.currentItem.mapData == undefined) {
            return false;
        } else {
            mapData = JSON.parse(this.state.currentItem.mapData) as IMapData;
            const requiresFixedTaskPosition = this.state.currentItem.typeId !== "Råtræ";
            return (requiresFixedTaskPosition ? mapData.fixedTaskPosition != undefined : true)
                && mapData.workingAreas.length > 0;
        }
    }

    private hasMapBeenChangedAfterDocumentationProcess() {
        let ci = this.state.currentItem;
        let result = false;

        // If both are undefined, not processed yet OR items before this new feature
        if (!ci.mapTimestamp && !ci.documentationTimestamp) {
            result = false;
        }

        // If maptimestamp is found and documentation timestamp is undefined, map has been changed since this new feature rolled out. Block!
        if (ci.mapTimestamp && !ci.documentationTimestamp) {
            result = true;
        }

        // If both maptimestamp and documentation timestamp are set, check if map has been changed after documentation. Block!
        if (ci.mapTimestamp && ci.documentationTimestamp) {
            result = ci.mapTimestamp.getTime() > ci.documentationTimestamp.getTime()
        }

        // If no files have been added, do not show warning yet
        if (!ci.files || ci.files.filter(d => d.type == "Documentation").length == 0) {
            result = false;
        }

        this.setState({ showDocumentationOutdatedWarning: result });
    }

    //#region Validate form
    private async validateForm() {
        let extendedCreation = false;

        // Hvis leverandør ikke er certificeret eller hvis "NEJ" er valgt (eks. på vegne af en leverandør eller en certificeret leverandør)
        if ((this.state.certifications?.length == 1 && this.state.currentItem.certification == undefined) || this.state.currentItem.certification && this.state.currentItem.certification.name == "Træet er forberedt til Dalgas SBP certificering") {
            extendedCreation = this.state.currentItem.biomassCategory?.startsWith("Skov:") || this.state.currentItem.biomassCategory?.startsWith("Ikke skov:") as boolean;
        }

        if (extendedCreation) {
            if (this.state.SBP_Persons.length == 0) {
                let SBP_Persons = [] as ISBPPersonDTO[];

                if (this.props.allowSelectionOfSupplier || App.IsUserInRole("DL") || App.IsUserInRole("Purchaser") || App.IsUserInRole("Admin")) {
                    if (this.state.currentItem.supplierID) {
                        SBP_Persons = await this.userService.GetSBPPersons(this.state.currentItem.supplierID + "");
                    }
                } else {
                    SBP_Persons = await this.userService.GetSBPPersonsForCurrentSupplier();
                }

                SBP_Persons = SBP_Persons.filter(x => x.educated != null && x.educated == true);

                this.EnsureFreeFormInputsInArray(SBP_Persons, "responsible");
                this.EnsureFreeFormInputsInArray(SBP_Persons, "trappingWorkPerformedByEntrepreneur");
                this.EnsureFreeFormInputsInArray(SBP_Persons, "extractionPerformedByEntrepreneur");
                this.EnsureFreeFormInputsInArray(SBP_Persons, "woodchippingPerformedByEntrepreneur");


                this.setState({ SBP_Persons: SBP_Persons });
            }
        }

        this.setState(
            {
                extendedCreationRequired: extendedCreation
            }
        );
    }

    private validateForm_CanApprove() {
        let isValid = false;
        let isFlis = this.state.currentItem.typeId === "Flis";

        isValid = Utilities.CheckNotNullOrEmpty(this.state.currentItem.orderName) &&
            (isFlis ? Utilities.CheckNotNullOrEmpty(this.state.currentItem.biomassCategory) : true) &&
            (isFlis ? Utilities.CheckNotNullOrEmpty(this.state.currentItem.description) : true) &&
            (isFlis ? Utilities.CheckNotNullOrEmpty(this.state.currentItem.ressourceType) : true) &&
            Utilities.CheckNotNullOrEmpty(this.state.currentItem.hdaoNo?.toLocaleString()) &&            
            this.state.currentItem.trappingDate != undefined && this.isMapValidForApproval();

        // Validate "Estimated amount", if Flis
        if(isValid && isFlis)
        {
            isValid = this.state.currentItem.estimatedAmount != null && Utilities.CheckNotNullOrEmpty(isNaN(this.state.currentItem.estimatedAmount as number) ? "" : this.state.currentItem.estimatedAmount + "");         
        }

        let mapData: IMapData | undefined = undefined;

        if (this.state.currentItem.mapData != undefined) {
            mapData = JSON.parse(this.state.currentItem.mapData) as IMapData;

            if (isValid) {
                isValid = mapData.stacks.some((x: IStackMarker, i, l) => l.filter(f => f.no == x.no).length > 1) === false;
            }
        }

        if (!isValid) {
            let missingFields = [] as string[];

            if (!Utilities.CheckNotNullOrEmpty(this.state.currentItem.orderName)) {
                missingFields.push("<li>Opgavenavn</li>");
            }
            if (isFlis && !Utilities.CheckNotNullOrEmpty(this.state.currentItem.biomassCategory)) {
                missingFields.push("<li>Flistype</li>");
            }
            if (isFlis && !Utilities.CheckNotNullOrEmpty(this.state.currentItem.description)) {
                missingFields.push("<li>Opgavebeskrivelse</li>");
            }
            if (isFlis && !Utilities.CheckNotNullOrEmpty(this.state.currentItem.ressourceType)) {
                missingFields.push("<li>Ressourcetype</li>");
            }
            if (!Utilities.CheckNotNullOrEmpty(this.state.currentItem.hdaoNo?.toLocaleString())) {
                missingFields.push("<li>Dalgas AO nr.</li>");
            }
            if (isFlis && (this.state.currentItem.estimatedAmount == null || !Utilities.CheckNotNullOrEmpty(isNaN(this.state.currentItem.estimatedAmount as number) ? "" : this.state.currentItem.estimatedAmount + ""))) {
                missingFields.push("<li>Estimeret mængde</li>");
            }
            if (this.state.currentItem.trappingDate == undefined) {
                missingFields.push("<li>Fældedato</li>");
            }

            if (!mapData) {
                missingFields.push("<li>Tegn kort</li>");
            }
            else {
                if (mapData.fixedTaskPosition == undefined && this.state.currentItem.typeId !== "Råtræ") {
                    missingFields.push("<li>Opgave GPS-punkt på kortet</li>");
                }

                if (mapData.workingAreas.length == 0) {
                    missingFields.push("<li>Min. 1 arbejdsområde eller linje på kortet</li>");
                }

                if (mapData.stacks.length > 0) {
                    const anyDuplicates = mapData.stacks.some((x: IStackMarker, i, l) => l.filter(f => f.no == x.no).length > 1);

                    if (anyDuplicates) {
                        missingFields.push("<li>Alle stakke på kortet skal have tildelt et unikt nr.</li>");
                    }
                }
            }

            if (!Utilities.CheckNotNullOrEmpty(this.state.currentItem.projectNo)) {
                missingFields.push("<li>Projektnummer</li>");
            }

            if (missingFields.length > 0) {
                let validationMessage = `<div>Du skal udfylde alle obligatoriske felter for at fortsætte. Du mangler: </div><ul>${missingFields.join("")}</ul>`;
                this.setState({ showValidationBox: true, validationMessage: validationMessage });
            }
            else {
                this.setState({ showValidationBox: false, validationMessage: "" });
            }
        }

        return isValid;
    }

    //#region Metoder
    public GroupSuppliers() {
        let options = [] as IComboBoxOption[];

        for (let item of this.state.suppliers.sort(this.SortOnRegion)) {
            if (options.filter(d => d.key == item.region?.name).length == 0) {
                options.push(
                    {
                        key: item.region?.name,
                        text: item.region?.name,
                        itemType: SelectableOptionMenuItemType.Header
                    } as IComboBoxOption
                );

                for (let child of this.state.suppliers.filter(d => d.region?.name == item.region?.name)) {
                    options.push(
                        {
                            key: child.id,
                            text: child.name,
                        } as IComboBoxOption
                    );
                }
            }
        }

        return options;
    }

    private getVolumeDescription(): string {
        let text = `Kortet summerer til: ${this.state.stackSumFromMap} rm`;

        if (this.state.stackTreeSpecices != "") {
            text = `${text} (${this.state.stackTreeSpecices})`;
        }

        return text;
    }

    private getTreeSpecicesUsedInStacks(mapData: IMapData): string {
        let all = mapData.stacks.filter(d => d.treeSpecices).map((v, i, a) => { return v.treeSpecices; });
        let treeSpecices = all.filter((item, index) => all.indexOf(item) === index);

        return treeSpecices.join(", ").toLowerCase();
    }

    private SortOnRegion(a: ICreateOrUpdateSupplierDTO, b: ICreateOrUpdateSupplierDTO) {
        let aRegion = a.region ? a.region?.name : "";
        let bRegion = b.region ? b.region?.name : "";

        if (aRegion > bRegion) return 1;
        if (bRegion > aRegion) return -1;

        return 0;
    }

    private EnsureFreeFormInputsInArray(SBP_Persons: ISBPPersonDTO[], field: string) {
        let currentAssignmentTemp = (this.state.currentItem as any);

        if (currentAssignmentTemp[field] && !SBP_Persons.find(d => d.id?.toLocaleLowerCase() == currentAssignmentTemp[field]?.toLocaleLowerCase())) {
            SBP_Persons.push({ id: currentAssignmentTemp[field], name: `${currentAssignmentTemp[field]} (indtastet manuelt)` });
        }
    }

    private async onSupplierChange(items: ITag[] | undefined) {
        if (items && items.length > 0) {
            let supplierId = items[0].key.toString();
            let projectNo = this.state.suppliers.filter(d => d.id == supplierId)[0].projectNo;

            this.updateProperty({ supplierID: supplierId, projectNo: projectNo });

            let certifications = await this.userService.GetCertificationsForSupplier(supplierId);

            this.setState({
                certifications: certifications
            });
        }

    }

    private onSBPPersonSelectionChange(field: string, event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void {
        let tempObject = {} as any;

        if (option) {
            tempObject[field] = option.key.toString();
        } else {
            this.setState({
                SBP_Persons: [...this.state.SBP_Persons, { id: value?.toLocaleLowerCase() + "", name: value + "" }]
            });

            tempObject[field] = value;
        }

        this.updateProperty(tempObject);
    }

    private async updateProperty(entity: Partial<IAssignmentDTO>): Promise<void> {
        this.setState(
            { currentItem: { ...this.state.currentItem, ...entity } },
            () => {
                this.validateForm();
                this.markUnsaved();
            }
        );
    }

    //#region Assignment handling
    private async deleteAssignment() {
        if (window.confirm("Er du sikker på, at du ønsker at slette denne opgave?")) {
            let result: boolean = false;
            if (this.props.selectedAssignment) {
                result = await this.assignmentService.DeleteAssignment({ ID: this.state.currentItem.id + "" });
            }

            if (result) {
                this.props.onChanged(this.state.currentItem, false);
            } else {
                alert("Der er sket en fejl under sletning af opgaven. Prøv venligst igen senere.");
            }
        }
    }

    private generateOfflineId(): string {
        return 'offline_' + new Date().toISOString();
    }

    private async handleDeleteOfflineAssignment(offlineId?: string): Promise<void> {
        const keyOfflineId = offlineId || this.state.currentItem.offlineId;
        const key = `offlineAssignment_${keyOfflineId}`;
        if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            this.props.onDismiss();
        } else {
            console.log(`Ingen offline opgave fundet med offlineId ${keyOfflineId}.`);
        }
    }

    private saveAssignmentOffline(): void {
        let currentItem = { ...this.state.currentItem };
        if (!currentItem.offlineId) {
            currentItem.offlineId = this.generateOfflineId();
            this.setState({ currentItem });
        }
        const offlineKey = 'offlineAssignment_' + currentItem.offlineId;
        const offlineItem: Partial<IAssignmentDTO> = { ...currentItem, status: 'Offline', typeId: currentItem.typeId, createdBy: App.Username };
        localStorage.setItem(offlineKey, JSON.stringify(offlineItem));
    }

    private async saveOfflineAssignmentToDB(currentItem: IAssignmentDTO): Promise<boolean> {
        currentItem.status = Status.WaitingForApproval;
        const { offlineId, ...assignmentForDb } = currentItem;
        const result = await this.assignmentService.CreateAssignment(assignmentForDb);
        if (result) {
            this.handleDeleteOfflineAssignment(offlineId ?? undefined);
            console.log(`Offline opgave med offlineId ${offlineId} er blevet overført til serveren.`);
            return true;
        } else {
            console.log(`Der opstod en fejl under overførsel af offline opgave med offlineId ${offlineId}.`);
            return false;
        }
    }

    private async onAddOrUpdateAssignment(currentItem: IAssignmentDTO, keepPanelOpen: boolean = false) {
        this.setState({ isSaving: true }, async () => {
            let result: boolean = false;

            if (!navigator.onLine) {
                this.saveAssignmentOffline();
                result = true;
            }

            if (currentItem.status != Status.Offline) {
                if (currentItem.id != undefined) {
                    result = await this.assignmentService.UpdateAssignment(currentItem);
                } else {
                    let newId = await this.assignmentService.CreateAssignment(currentItem);
                    result = newId != "";
                }
            } else {
                result = await this.saveOfflineAssignmentToDB(currentItem);
            }

            if (result) {
                this.props.onChanged(currentItem, keepPanelOpen);
            } else {
                let errorMessage = this.props.selectedAssignment ? "Der er sket en fejl under opdateringen af opgaven. Prøv venligst igen senere." : "Der er sket en fejl under oprettelse af opgaven. Prøv venligst igen senere.";
                alert(errorMessage);
            }

            this.setState({ isSaving: false });
        });
    }

    private async takeNextPINFONo() {
        let result = await this.assignmentService.TakeNextPINFONo(this.state.currentItem);

        this.setState({ currentItem: { ...this.state.currentItem, hdaoNo: result } });
    }

    private async downloadFile(fileName: string) {
        let href = await this.assignmentService.DownloadFile(this.state.currentItem, fileName);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private async preSaveAssignment() {
        let newId = await this.assignmentService.CreateAssignment(this.state.currentItem);

        if (newId != "") {
            this.updateProperty({ id: newId })
        } else {
            alert("Der er sket en fejl under oprettelse af opgaven. Prøv venligst igen senere.");
        }
    }

    private async deleteFile(file: IAssignmentFileDTO) {
        await this.assignmentService.DeleteFile(this.state.currentItem, file);

        let files = this.state.currentItem.files?.filter(d => d !== file);

        this.updateProperty({ files: files })
    }

    private async addFiles(eventFiles: FileList, type: string) {
        let files = this.state.currentItem.files;

        let promises = Array.from(eventFiles).map(file => {

            // Define a new file reader
            let reader = new FileReader();

            // Create a new promise
            return new Promise(resolve => {

                reader.readAsDataURL(file);

                // Resolve the promise after reading file
                reader.onload = () => {
                    files?.push({ name: file.name, content: reader.result + "", type: type, isNew: true });
                    resolve(reader.result);
                }
            });
        });

        await Promise.all(promises);

        this.updateProperty({ files: files });
    }

    private trimOrderName(newValue: string): string {
        return newValue.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '');
    }

    private hasUnsavedChanges: boolean = false;

    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('popstate', this.handlePopState);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('popstate', this.handlePopState);
    }

    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.shouldWarnOnLeave()) {
            e.preventDefault();
            e.returnValue = '';
            return '';
        }
    };

    handlePopState = (e: PopStateEvent) => {
        if (this.shouldWarnOnLeave()) {
            this.setState({ showLeaveConfirmDialog: true });
            window.history.pushState(null, '', window.location.href);
        }
    };

    shouldWarnOnLeave = () => {
        return (this.state.mode === FormMode.New || this.state.mode === FormMode.Edit) && this.hasUnsavedChanges;
    };

    markUnsaved = () => {
        if (!this.hasUnsavedChanges) {
            this.hasUnsavedChanges = true;
        }
    };

    clearUnsaved = () => {
        this.hasUnsavedChanges = false;
    };

    confirmLeave = () => {
        this.setState({ showLeaveConfirmDialog: false }, () => {
            this.clearUnsaved();
            window.removeEventListener('popstate', this.handlePopState);
            window.history.back();
        });
    };
}

export default withLanguage(AssignmentPanel);